import type { AppNotification } from '@/types'
import { formatCurrency } from './format'

type TranslationOptions = Record<string, unknown> & { defaultValue?: string }
type TranslationFn = (key: string, options?: TranslationOptions) => string

function stringValue(data: Record<string, unknown> | null, key: string) {
  const value = data?.[key]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function numberValue(data: Record<string, unknown> | null, key: string) {
  const value = data?.[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const normalized = value.trim().replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function notificationAmount(data: Record<string, unknown> | null) {
  const label = stringValue(data, 'amount')
  if (label && /[^\d\s.,-]/.test(label)) return label

  const amount = numberValue(data, 'amount')
  if (amount !== null) return formatCurrency(amount, 'EUR', 2)

  return label
}

function displayLabel(value: string | null) {
  if (!value) return null

  const label = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return label ? `${label.charAt(0).toUpperCase()}${label.slice(1)}` : null
}

function timeValue(data: Record<string, unknown> | null) {
  const raw =
    stringValue(data, 'start_time') ??
    stringValue(data, 'callback_time') ??
    stringValue(data, 'time')

  return raw?.slice(0, 5) ?? null
}

function displayLocale(language: string) {
  return language.startsWith('fr') ? 'fr-FR' : 'en-US'
}

function monthDay(value: string | null, language: string) {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat(displayLocale(language), {
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function notificationOptions(notification: AppNotification, language: string) {
  const data = notification.data
  const reference =
    stringValue(data, 'reference') ??
    stringValue(data, 'quote_reference') ??
    stringValue(data, 'invoice_reference') ??
    stringValue(data, 'payment_reference')
  const client =
    stringValue(data, 'client_name') ??
    stringValue(data, 'client') ??
    stringValue(data, 'customer_name')
  const date = monthDay(
    stringValue(data, 'scheduled_date') ?? stringValue(data, 'due_date'),
    language
  )
  const time = timeValue(data)
  const amount = notificationAmount(data)
  const daysOverdue = numberValue(data, 'days_overdue')
  const assignee = stringValue(data, 'assignee') ?? ''

  return {
    reference: reference ?? '',
    client: client ?? '',
    date: date ?? '',
    time: time ?? '',
    amount: amount ?? '',
    days: daysOverdue !== null ? String(daysOverdue) : '',
    assignee,
    title:
      stringValue(data, 'task_title') ??
      stringValue(data, 'title') ??
      notification.title,
    defaultValue: '',
  }
}

function hasDescriptionData(notification: AppNotification) {
  const data = notification.data
  if (notification.type === 'job_assigned') {
    return Boolean(
      (stringValue(data, 'client_name') ?? stringValue(data, 'client')) &&
      (stringValue(data, 'scheduled_date') ?? stringValue(data, 'due_date')) &&
      timeValue(data)
    )
  }

  if (notification.type?.startsWith('task_')) {
    return Boolean(
      stringValue(data, 'task_title') ?? stringValue(data, 'title')
    )
  }

  return Boolean(
    stringValue(data, 'reference') ??
    stringValue(data, 'quote_reference') ??
    stringValue(data, 'invoice_reference') ??
    stringValue(data, 'payment_reference') ??
    stringValue(data, 'client_name') ??
    stringValue(data, 'client')
  )
}

export function notificationTitle(
  notification: AppNotification,
  t: TranslationFn,
  language: string
) {
  if (notification.type) {
    const title = t(
      `events.${notification.type}.title`,
      notificationOptions(notification, language)
    )
    if (title) return title
  }

  return notification.title
}

export function notificationDescription(
  notification: AppNotification,
  t: TranslationFn,
  language: string
) {
  if (notification.type && hasDescriptionData(notification)) {
    const description = t(
      `events.${notification.type}.description`,
      notificationOptions(notification, language)
    )
    if (description) return description
  }

  return notification.description ?? ''
}

export function notificationMeta(notification: AppNotification) {
  const data = notification.data
  const postalCode =
    stringValue(data, 'postal_code') ?? stringValue(data, 'zip_code')
  const city = stringValue(data, 'city')
  const location = [postalCode, city].filter(Boolean).join(' ') || null
  const serviceCategory =
    stringValue(data, 'service_category') ??
    stringValue(data, 'service_category_name') ??
    stringValue(data, 'service_type') ??
    stringValue(data, 'pest_name') ??
    stringValue(data, 'pest') ??
    stringValue(data, 'pest_type')
  const interventionType =
    stringValue(data, 'intervention_type_label') ??
    stringValue(data, 'intervention_type') ??
    stringValue(data, 'service_name') ??
    stringValue(data, 'job_type')

  return [
    location,
    displayLabel(serviceCategory),
    displayLabel(interventionType),
  ].filter((value): value is string => Boolean(value))
}

export function relativeNotificationTime(
  value: string | null | undefined,
  language: string
) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  const minutes = Math.floor(seconds / 60)
  const english = language.startsWith('en')

  if (minutes < 1) return english ? 'just now' : 'maintenant'
  if (minutes < 60) return english ? `${minutes} min ago` : `${minutes} min`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return english ? `${hours} h ago` : `${hours} h`

  const days = Math.floor(hours / 24)
  if (days < 7) return english ? `${days} d ago` : `${days} j`

  return date.toLocaleDateString(displayLocale(language))
}

export function notificationTimestamp(
  value: string | null | undefined,
  language: string
) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleString(displayLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
